import { useEffect, useState } from "react";
import { Printer, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

export const PrintDialog = ({ labelIds, open, onOpenChange, onCompleted, onReplaceIds }) => {
  const [job, setJob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [clashes, setClashes] = useState(null);

  const createJob = async (approve) => {
    setLoading(true);
    setClashes(null);
    try {
      const { data } = await api.post("/print-jobs", { label_ids: labelIds, approve });
      setJob(data);
      const res = await api.get(`/print-jobs/${data.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfUrl(url);
      if (approve) toast.warning(`Printed with ${data.approved_clashes} approved clash(es) — logged under your account`);
    } catch (e) {
      const detail = e.response?.status === 409 ? e.response.data?.detail : null;
      if (detail?.code === "shipped_guard") setClashes(detail.clashes);
      else toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !labelIds?.length) return;
    createJob(false);
    return () => { setJob(null); setPdfUrl((u) => { if (u) URL.revokeObjectURL(u); return null; }); setClashes(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, labelIds]);

  const printWithoutClashes = () => {
    const bad = new Set(clashes.map((c) => c.label_id));
    const rest = (labelIds || []).filter((id) => !bad.has(id));
    if (!rest.length) return toast.error("Every selected label clashes — nothing left to print");
    onReplaceIds?.(rest);
  };

  const total = labelIds?.length ?? 0;
  if (!open) return null;

  const doPrint = () => {
    const frame = document.getElementById("print-frame");
    if (frame?.contentWindow && window.innerWidth >= 768) {
      const win = frame.contentWindow;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        win.removeEventListener("afterprint", finish);
        window.removeEventListener("focus", onFocus);
        complete(true);
      };
      const onFocus = () => setTimeout(finish, 300);
      win.addEventListener("afterprint", finish);
      setTimeout(() => window.addEventListener("focus", onFocus), 500);
      win.focus();
      win.print();
    } else if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  const undo = async (jobId) => {
    try {
      const { data } = await api.post(`/print-jobs/${jobId}/undo`);
      toast.info(`${data.reverted} label(s) moved back to pending`);
      onCompleted?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const complete = async (auto = false) => {
    if (!job) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/print-jobs/${job.id}/complete`);
      toast.success(`${data.printed} label(s) marked as printed`, auto ? {
        duration: 10000,
        description: "Cancelled the print? Undo to move them back to pending.",
        action: { label: "Undo", onClick: () => undo(job.id) },
      } : undefined);
      onCompleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="print-dialog" className="max-h-[92vh] max-w-5xl overflow-hidden border-slate-800 bg-[#111827] p-0">
        <DialogHeader className="border-b border-slate-800 p-5">
          <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-tight">Print labels</DialogTitle>
          <DialogDescription className="text-slate-400">
            {clashes ? "Shipped guard blocked this print." : job ? `${job.count} page(s) merged in Warehouse → SKU order.` : "Preparing merged PDF…"} {!clashes && "Print now marks labels printed automatically when the printer dialog closes (Undo available)."}
          </DialogDescription>
        </DialogHeader>
        {clashes ? (
          <div className="max-h-[60vh] overflow-y-auto p-5" data-testid="shipped-guard-panel">
            <div className="flex items-start gap-3 rounded-md border border-red-700/60 bg-red-950/40 p-4 text-red-100">
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
              <div className="text-sm"><strong>{clashes.length}</strong> of the selected labels share a tracking ID with a label that was <strong>already printed</strong>. Printing them again may ship the same order twice.</div>
            </div>
            <div className="mt-4 overflow-x-auto rounded-md border border-slate-800">
              <table className="w-full text-xs">
                <thead><tr className="font-mono text-[10px] uppercase tracking-widest text-slate-400"><th className="p-2 text-left">Label</th><th className="p-2 text-left">Order / Tracking</th><th className="p-2 text-left">Clashes with (already printed)</th></tr></thead>
                <tbody>
                  {clashes.map((c) => (
                    <tr key={c.label_id} className="border-t border-slate-800" data-testid={`clash-row-${c.label_id}`}>
                      <td className="p-2 font-mono"><span className="text-blue-300">{c.warehouse_id || "—"}</span> {c.sku_id}</td>
                      <td className="p-2 font-mono text-slate-300">{c.order_id || "—"}<div className="text-slate-500">{c.tracking_id || ""}</div></td>
                      <td className="p-2 text-slate-300">{c.twin_file} · page {c.twin_page} · <span className="uppercase">{c.twin_marketplace}</span><div className="text-slate-500">printed {c.twin_printed_at ? new Date(c.twin_printed_at).toLocaleString() : ""}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="outline" className="border-slate-700" onClick={() => onOpenChange(false)} data-testid="guard-cancel-button">Cancel</Button>
              {onReplaceIds && clashes.length < total && (
                <Button variant="outline" className="border-slate-700" onClick={printWithoutClashes} data-testid="guard-skip-button">Print the other {total - clashes.length} only</Button>
              )}
              <Button className="bg-red-600 hover:bg-red-700" onClick={() => createJob(true)} data-testid="guard-approve-button"><ShieldAlert className="mr-2 h-4 w-4" /> Approve & print anyway</Button>
            </div>
          </div>
        ) : (
        <div className="grid h-[60vh] gap-0 md:grid-cols-[1fr_280px]">
          <div className="hidden h-full bg-slate-950 md:block">
            {loading || !pdfUrl ? (
              <div className="flex h-full items-center justify-center text-slate-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Building PDF…</div>
            ) : (
              <iframe id="print-frame" title="Labels PDF" src={pdfUrl} className="h-full w-full" data-testid="print-pdf-frame" />
            )}
          </div>
          <div className="flex h-full min-h-0 flex-col border-l border-slate-800">
            <div className="font-mono px-4 pt-4 text-[11px] uppercase tracking-widest text-slate-400">Print order</div>
            <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto p-4 pt-2" data-testid="print-order-list">
              {job?.labels?.map((l, i) => (
                <div key={l.id} className="flex items-center gap-2 border-b border-slate-800/60 py-1.5 text-xs">
                  <span className="w-6 font-mono text-slate-500">{i + 1}</span>
                  <span className={l.warehouse_id ? "font-mono text-blue-300" : "font-mono text-red-300"}>{l.warehouse_id || "UNMATCHED"}</span>
                  <span className="truncate font-mono text-slate-200">{l.master_sku || l.sku_id || "unreadable SKU"}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-slate-800 p-4">
              <Button data-testid="print-now-button" className="w-full bg-blue-600 hover:bg-blue-700" disabled={!pdfUrl} onClick={doPrint}>
                <Printer className="mr-2 h-4 w-4" /> Print now
              </Button>
              <Button data-testid="print-open-tab-button" variant="outline" className="w-full border-slate-700" disabled={!pdfUrl} onClick={() => window.open(pdfUrl, "_blank")}>
                Open PDF in new tab
              </Button>
              <Button data-testid="print-confirm-button" variant="outline" className="w-full border-emerald-700 text-emerald-300 hover:bg-emerald-900/40"
                disabled={!job || completing} onClick={() => complete(false)}>
                {completing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Mark as printed
              </Button>
            </div>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
